import { useEffect, useState, useCallback } from 'react';
import { notification } from 'antd';

import { useGet, usePost, usePut } from 'hooks';
import { netPromoterScore, netPromoterScorePrompt } from 'services/api';

import Content from './Content';

const statusEnums = {
  NO_RESPONSE: 1,
  RESPONDED: 2,
  DECLINED: 3,
};

const NOTIFICATION_KEY = 'npsPrompt';

const NetPromoterScore = ({ onDismiss }) => {
  const [currentPrompt, setCurrentPrompt] = useState(null);
  const [submittedScore, setSubmittedScore] = useState(null);
  const [shouldClose, setShouldClose] = useState(false);
  const [hasSubmittedFeedback, setHasSubmittedFeedback] = useState(false);

  const [{ res: canShowNps }] = useGet({
    url: netPromoterScorePrompt,
    onMount: true,
  });

  const [{ res: npsPrompt }, postNps] = usePost({
    url: netPromoterScore,
  });

  const [{ res: npsUpdate }, putNps] = usePut({
    url: netPromoterScore,
  });

  useEffect(() => {
    if (canShowNps && !currentPrompt && !npsPrompt) {
      postNps();
    }
  }, [canShowNps, currentPrompt, npsPrompt, postNps]);

  const handleNpsScore = useCallback(
    score => {
      setSubmittedScore(score);
      if (currentPrompt?.id) {
        putNps({
          id: currentPrompt.id,
          statusId: statusEnums.RESPONDED,
          score,
        });
      }
    },
    [currentPrompt, putNps]
  );

  const handleNpsFeedback = useCallback(
    feedback => {
      setHasSubmittedFeedback(true);
      setShouldClose(true);
      if (currentPrompt?.id) {
        putNps({
          id: currentPrompt.id,
          statusId: statusEnums.RESPONDED,
          feedbackmessage: feedback,
          score: submittedScore,
        });
      }
    },
    [currentPrompt, submittedScore, putNps]
  );

  const handleFeedbackDismiss = useCallback(() => {
    if (currentPrompt?.id && submittedScore !== null) {
      setShouldClose(true);
    }
  }, [currentPrompt, submittedScore]);

  const handleDismiss = useCallback(() => {
    // If feedback has been submitted OR a score has been submitted, just close without changing status
    if (hasSubmittedFeedback || submittedScore !== null) {
      notification.close(NOTIFICATION_KEY);
      onDismiss();
      return;
    }

    if (currentPrompt?.id) {
      setShouldClose(true);
      putNps({
        id: currentPrompt.id,
        statusId: statusEnums.DECLINED,
      });
    }
  }, [hasSubmittedFeedback, submittedScore, currentPrompt, putNps, onDismiss]);

  useEffect(() => {
    if (npsPrompt && !currentPrompt) {
      setCurrentPrompt(npsPrompt);
    }
  }, [npsPrompt, currentPrompt]);

  useEffect(() => {
    if (currentPrompt) {
      notification.open({
        placement: 'topRight',
        duration: 0,
        message: (
          <span style={{ fontSize: 15, fontWeight: 500 }}>
            How likely are you to recommend SciLeads?
          </span>
        ),
        description: (
          <Content
            onNpsScore={handleNpsScore}
            onFeedbackMessage={handleNpsFeedback}
            onFeedbackDismiss={handleFeedbackDismiss}
          />
        ),
        key: NOTIFICATION_KEY,
        onClose: handleDismiss,
      });
    }
  }, [
    currentPrompt,
    handleNpsScore,
    handleNpsFeedback,
    handleFeedbackDismiss,
    handleDismiss,
  ]);

  useEffect(() => {
    if (npsUpdate && shouldClose) {
      notification.close(NOTIFICATION_KEY);
      setShouldClose(false);
      onDismiss();
    }
  }, [npsUpdate, shouldClose, onDismiss]);

  return null;
};

export default NetPromoterScore;
